import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Search, Trash2, Mail, Phone, Clock, Filter, MessageSquare } from 'lucide-react';
import { adminGetLeads, adminUpdateLeadStatus, adminDeleteLead, getServices } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const statusOptions = [
  { value: 'neu', label: 'E Re', class: 'bg-[#D4B982] text-[#0A0A0A]' },
  { value: 'in_bearbeitung', label: 'Në Proces', class: 'bg-blue-100 text-blue-800' },
  { value: 'erledigt', label: 'E Përfunduar', class: 'bg-green-100 text-green-800' },
];

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchData = async () => {
    try {
      const [leadsRes, servicesRes] = await Promise.all([
        adminGetLeads(),
        getServices()
      ]);
      setLeads(leadsRes.data);
      setFilteredLeads(leadsRes.data);
      setServices(servicesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Gabim gjatë marrjes së të dhënave');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = leads;

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(l => 
        l.name.toLowerCase().includes(search) ||
        l.email.toLowerCase().includes(search) ||
        l.phone.toLowerCase().includes(search)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(l => l.status === statusFilter);
    }

    setFilteredLeads(filtered);
  }, [searchTerm, statusFilter, leads]);

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await adminUpdateLeadStatus(leadId, newStatus);
      toast.success('Statusi u përditësua');
      fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Gabim gjatë përditësimit të statusit');
    }
  };

  const handleOpenDelete = (lead) => {
    setSelectedLead(lead);
    setIsDeleteDialogOpen(true);
  };

  const handleOpenDetail = (lead) => {
    setSelectedLead(lead);
    setIsDetailDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedLead) return;

    try {
      await adminDeleteLead(selectedLead.id);
      toast.success('Kërkesa u fshi me sukses!');
      setIsDeleteDialogOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error deleting lead:', err);
      toast.error('Gabim gjatë fshirjes së kërkesës');
    }
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    return service?.title_de || '-';
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(s => s.value === status) || statusOptions[0];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="admin-leads-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0A0A0A]">Kërkesat</h1>
        <p className="text-[#71717A]">Menaxho kërkesat e klientëve</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={18} />
          <Input
            type="text"
            placeholder="Kërko (emër, email, telefon)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-leads"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-[#71717A]" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="filter-status">
              <SelectValue placeholder="Filtro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Të gjitha</SelectItem>
              {statusOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leads List */}
      <div className="space-y-4">
        {filteredLeads.map((lead) => {
          const statusInfo = getStatusInfo(lead.status);
          return (
            <div 
              key={lead.id} 
              className="bg-white p-6 rounded-sm border border-[#E4E4E7] hover:border-[#D4B982] transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-[#0A0A0A] text-lg">{lead.name}</h3>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusInfo.class}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-[#71717A] mb-3">
                    <a href={`mailto:${lead.email}`} className="flex items-center gap-1 hover:text-[#D4B982]">
                      <Mail size={14} />
                      {lead.email}
                    </a>
                    <a href={`tel:${lead.phone}`} className="flex items-center gap-1 hover:text-[#D4B982]">
                      <Phone size={14} />
                      {lead.phone}
                    </a>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatDateTime(lead.created_at)}
                    </span>
                  </div>

                  {lead.service_id && (
                    <p className="text-sm text-[#0A0A0A] mb-2">
                      <span className="text-[#71717A]">Shërbimi:</span> {getServiceName(lead.service_id)}
                    </p>
                  )}

                  <p className="text-sm text-[#71717A] line-clamp-2">{lead.message}</p>
                </div>

                <div className="flex items-center gap-3">
                  <Select 
                    value={lead.status} 
                    onValueChange={(value) => handleStatusChange(lead.id, value)}
                  >
                    <SelectTrigger className="w-36" data-testid={`lead-status-${lead.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDetail(lead)}
                    className="text-[#71717A] hover:text-[#D4B982]"
                    data-testid={`view-lead-${lead.id}`}
                  >
                    <MessageSquare size={16} />
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleOpenDelete(lead)}
                    className="text-[#71717A] hover:text-red-500"
                    data-testid={`delete-lead-${lead.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}

        {filteredLeads.length === 0 && (
          <div className="bg-white p-12 text-center rounded-sm border border-[#E4E4E7]">
            <MessageSquare className="mx-auto text-[#71717A] mb-4" size={48} />
            <p className="text-[#71717A]">
              {searchTerm || statusFilter !== 'all' 
                ? 'Nuk u gjet asnjë kërkesë' 
                : 'Nuk ka kërkesa ende'}
            </p>
          </div>
        )}
      </div>

      {/* Lead Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Detajet e Kërkesës</DialogTitle>
          </DialogHeader>

          {selectedLead && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#71717A]">Emri</p>
                  <p className="font-medium text-[#0A0A0A]">{selectedLead.name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#71717A]">Statusi</p>
                  <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getStatusInfo(selectedLead.status).class}`}>
                    {getStatusInfo(selectedLead.status).label}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#71717A]">Email</p>
                  <a href={`mailto:${selectedLead.email}`} className="font-medium text-[#D4B982] hover:underline">
                    {selectedLead.email}
                  </a>
                </div>
                <div>
                  <p className="text-sm text-[#71717A]">Telefon</p>
                  <a href={`tel:${selectedLead.phone}`} className="font-medium text-[#D4B982] hover:underline">
                    {selectedLead.phone}
                  </a>
                </div>
              </div>

              {selectedLead.service_id && (
                <div>
                  <p className="text-sm text-[#71717A]">Shërbimi i kërkuar</p>
                  <p className="font-medium text-[#0A0A0A]">{getServiceName(selectedLead.service_id)}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-[#71717A]">Data</p>
                <p className="font-medium text-[#0A0A0A]">{formatDateTime(selectedLead.created_at)}</p>
              </div>

              <div>
                <p className="text-sm text-[#71717A] mb-2">Mesazhi</p>
                <div className="bg-[#F4F4F5] p-4 rounded-sm">
                  <p className="text-[#0A0A0A] whitespace-pre-wrap">{selectedLead.message}</p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <a
                  href={`mailto:${selectedLead.email}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] px-4 py-2 rounded-sm font-medium"
                >
                  <Mail size={16} />
                  Dërgo Email
                </a>
                <a
                  href={`tel:${selectedLead.phone}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#E4E4E7] hover:border-[#D4B982] text-[#0A0A0A] px-4 py-2 rounded-sm font-medium"
                >
                  <Phone size={16} />
                  Telefono
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konfirmo Fshirjen</AlertDialogTitle>
            <AlertDialogDescription>
              Jeni të sigurt që dëshironi të fshini këtë kërkesë nga "{selectedLead?.name}"? 
              Ky veprim nuk mund të kthehet prapa.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anulo</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-lead"
            >
              Fshi
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
